import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Palmtree, AlertCircle, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";

const LoginPage = () => {
  const [dpi, setDpi] = useState("");
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [blockedAlert, setBlockedAlert] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("blocked") === "true") {
      setBlockedAlert(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const savedDpi = localStorage.getItem("vgest_saved_dpi");
    const savedDate = localStorage.getItem("vgest_saved_date");
    if (savedDpi && savedDate) {
      setDpi(savedDpi);
      setFechaNacimiento(savedDate);
      setRememberMe(true);
    }
  }, []);

  const handleDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 8) value = value.substring(0, 8);
    
    if (value.length >= 5) {
      value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
    } else if (value.length >= 3) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`;
    }
    
    setFechaNacimiento(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let apiFecha = fechaNacimiento;
      if (fechaNacimiento.includes("/")) {
        const parts = fechaNacimiento.split("/");
        if (parts.length === 3) {
          apiFecha = `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }

      await login(dpi, apiFecha);
      
      if (rememberMe) {
        localStorage.setItem("vgest_saved_dpi", dpi);
        localStorage.setItem("vgest_saved_date", fechaNacimiento);
      } else {
        localStorage.removeItem("vgest_saved_dpi");
        localStorage.removeItem("vgest_saved_date");
      }

      toast.success("¡Bienvenido!");
      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.detail || "Credenciales inválidas. Verifique su DPI y fecha de nacimiento.");
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDatabase = async () => {
    setSeeding(true);
    try {
      const response = await api.seedDatabase();
      toast.success("Base de datos inicializada", {
        description: "Puede usar las credenciales de ejemplo para ingresar",
      });
      console.log("Seed response:", response.data);
    } catch (err) {
      console.error("Seed error:", err);
      toast.info("La base de datos ya tiene datos de ejemplo");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1770425616829-61d6912705ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBtaW5pbWFsJTIwYnJpZ2h0fGVufDB8fHx8MTc3MzI4NTQ0MHww&ixlib=rb-4.1.0&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-sky-500/20"></div>
        <div className="absolute bottom-12 left-12 right-12 bg-white/90 backdrop-blur-sm rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            Sistema de Gestión de Vacaciones
          </h2>
          <p className="text-slate-600">
            Administra tus días de descanso de forma simple y eficiente.
            Solicita, aprueba y visualiza las vacaciones de tu equipo en un solo lugar.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/10 p-1.5 bg-white border border-slate-100">
              <img src="/logo-cps.png" alt="CPS Logistics" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">CPS Time Off</h1>
              <p className="text-sm text-slate-500">Gestión de Vacaciones</p>
            </div>
          </div>

          <Card className="border-0 shadow-xl shadow-slate-200/50">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl">Iniciar Sesión</CardTitle>
              <CardDescription>
                Ingrese su DPI y fecha de nacimiento para acceder
              </CardDescription>
            </CardHeader>
            <CardContent>
              {blockedAlert && (
                <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-800 text-xs sm:text-sm flex items-start gap-2.5 shadow-sm">
                  <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="font-semibold block text-red-900">Acceso Bloqueado / Cuenta Inactiva</strong>
                    Esta cuenta ha sido desactivada en el sistema o ya no pertenece a la organización. Si se trata de un error, favor comunicarse con Recursos Humanos.
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="dpi">DPI (Documento de Identificación)</Label>
                  <Input
                    id="dpi"
                    type="text"
                    placeholder="Ej: 1234567890101"
                    value={dpi}
                    onChange={(e) => setDpi(e.target.value)}
                    required
                    className="h-12"
                    data-testid="login-dpi-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fechaNacimiento">Fecha de Nacimiento</Label>
                  <Input
                    id="fechaNacimiento"
                    type="tel"
                    placeholder="DD/MM/YYYY"
                    value={fechaNacimiento}
                    onChange={handleDateChange}
                    required
                    className="h-12"
                    data-testid="login-date-input"
                  />
                </div>

                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox 
                    id="remember" 
                    checked={rememberMe} 
                    onCheckedChange={setRememberMe} 
                  />
                  <Label htmlFor="remember" className="text-sm font-normal text-slate-600 cursor-pointer">
                    Recordar mis datos
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Ingresando...
                    </>
                  ) : (
                    "Ingresar"
                  )}
                </Button>
              </form>

              {/* 
              <div className="mt-6 pt-6 border-t border-slate-100">
                <p className="text-sm text-slate-500 text-center mb-4">
                  ¿Primera vez? Inicialice los datos de ejemplo
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleSeedDatabase}
                  disabled={seeding}
                  data-testid="seed-database-btn"
                >
                  {seeding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Inicializando...
                    </>
                  ) : (
                    "Cargar Datos de Ejemplo"
                  )}
                </Button>
              </div> 
              */}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
